import { Component } from '@angular/core';
import { ITooltipAngularComp } from 'ag-grid-angular';
import { ITooltipParams } from 'ag-grid-community';

interface SpecEntry {
  label: string;
  value: string;
}

@Component({
  selector: 'app-item-spec-tooltip',
  standalone: true,
  template: `
    <div class="spec-tooltip">
      @if (description) {
        <div class="spec-tooltip-section">
          <h4 class="spec-tooltip-title">Product Description</h4>
          <p class="spec-tooltip-desc">{{ description }}</p>
        </div>
      }
      @if (specs.length) {
        <div class="spec-tooltip-section">
          <h4 class="spec-tooltip-title">Specifications</h4>
          <div class="spec-grid">
            @for (spec of specs; track spec.label) {
              <div class="spec-label">{{ spec.label }}</div>
              <div class="spec-value">{{ spec.value }}</div>
            }
          </div>
        </div>
      }
    </div>
  `,
  styles: [
    `
      .spec-tooltip {
        background: #2d2d2d;
        color: rgba(255, 255, 255, 0.9);
        border: 1px solid rgba(255, 255, 255, 0.1);
        border-radius: 12px;
        padding: 16px 20px;
        width: 820px;
        max-width: 90vw;
        box-shadow: 0 10px 25px rgba(0, 0, 0, 0.4);
        font-size: 12px;
        line-height: 1.5;
        white-space: normal;
      }
      .spec-tooltip-section + .spec-tooltip-section {
        margin-top: 14px;
      }
      .spec-tooltip-title {
        margin: 0 0 8px;
        padding-bottom: 6px;
        font-size: 13px;
        font-weight: 500;
        color: #00a8ff;
        border-bottom: 2px solid #00a8ff;
      }
      .spec-tooltip-desc {
        margin: 0;
        color: rgba(255, 255, 255, 0.6);
      }
      .spec-grid {
        display: grid;
        grid-template-columns: auto 1fr auto 1fr;
        column-gap: 28px;
      }
      .spec-label {
        color: rgba(255, 255, 255, 0.5);
        padding: 10px 0;
        font-weight: 500;
        white-space: nowrap;
        border-bottom: 1px solid rgba(255, 255, 255, 0.07);
      }
      .spec-value {
        color: rgba(255, 255, 255, 0.95);
        padding: 10px 0;
        font-weight: 500;
        word-break: break-word;
        border-bottom: 1px solid rgba(255, 255, 255, 0.07);
      }
    `,
  ],
})
export class ItemSpecTooltipComponent implements ITooltipAngularComp {
  description = '';
  specs: SpecEntry[] = [];

  agInit(params: ITooltipParams): void {
    const data = params.data;
    if (!data) return;

    this.description = (data.desc2 || data.desc1 || '').trim();

    const specs: SpecEntry[] = [];
    const addSpec = (label: string, value: any, rawKey?: string) => {
      const formatted = this.formatValue(value, rawKey);
      if (formatted) specs.push({ label, value: formatted });
    };

    addSpec('UPC', data.upc);
    addSpec('Manufacturer', data.manufacturer);
    addSpec('Manufacturer Part #', data.mfg_part_number);

    const attributes = data.attributes;
    if (attributes && typeof attributes === 'object') {
      for (const key of Object.keys(attributes)) {
        addSpec(this.prettifyKey(key), attributes[key], key);
      }
    }

    addSpec('Subcategory', data.subcategory);

    this.specs = specs;
  }

  private prettifyKey(key: string): string {
    return key
      .replace(/_in$/, '')
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase());
  }

  private formatValue(value: any, key?: string): string {
    if (value === null || value === undefined || value === '') return '';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'number') {
      return key?.endsWith('_in') ? `${value}"` : String(value);
    }
    if (Array.isArray(value)) {
      return value.filter(Boolean).join(', ');
    }
    return String(value).trim();
  }
}
